(() => {
  'use strict';

  // Scroll-lock class; set on <html> (see frk-spec-badges.css)
  const BODY_CLASS = 'frk-spec-sheet-open';
  const lockRoot = () => document.documentElement;
  const COLLECTION_DIALOG_ID = 'frk-collection-specifications';

  const ICONS = {
    weight: '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="5" r="3"/><path d="M6.5 8a2 2 0 0 0-1.905 1.46L2.1 18.5A2 2 0 0 0 4 21h16a2 2 0 0 0 1.925-2.54L19.4 9.5A2 2 0 0 0 17.48 8Z"/></svg>',
    fire: '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>',
    warranty: '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
    coverage: '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 17l6-6 4 4 7-7"/><path d="M14 8h6v6"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    external: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true"><path d="M14 5h5v5M19 5l-8 8M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function ensureScrollCue(dialog) {
    if (!dialog) return null;

    const actions = dialog.querySelector('.frk-spec-sheet__actions');
    const content = dialog.querySelector('.frk-spec-sheet__content');
    if (!actions || !content) return null;

    let cue = actions.querySelector('.frk-spec-sheet__scroll-cue');
    if (!cue) {
      cue = document.createElement('div');
      cue.className = 'frk-spec-sheet__scroll-cue';
      cue.setAttribute('aria-hidden', 'true');
      cue.innerHTML = '<span aria-hidden="true">↓</span><span>Scroll for more</span>';
      actions.prepend(cue);
    }

    if (!content.__frkScrollCueBound) {
      content.__frkScrollCueBound = true;
      content.addEventListener('scroll', () => {
        if (content.scrollTop > 3) dialog.__frkSpecHasScrolled = true;
        updateScrollCue(dialog);
      }, { passive: true });
    }

    return cue;
  }

  function updateScrollCue(dialog) {
    if (!dialog) return;

    const content = dialog.querySelector('.frk-spec-sheet__content');
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const canScroll = Boolean(content && content.scrollHeight > content.clientHeight + 12);
    const show = isMobile && canScroll && !dialog.__frkSpecHasScrolled;

    dialog.classList.toggle('frk-spec-sheet--show-scroll-cue', show);
  }

  function resetScrollPosition(dialog) {
    if (!dialog) return;
    const content = dialog.querySelector('.frk-spec-sheet__content');
    dialog.__frkSpecHasScrolled = false;
    if (content) content.scrollTop = 0;
    ensureScrollCue(dialog);
    window.requestAnimationFrame(() => updateScrollCue(dialog));
  }

  function activate(dialog, target, focusTab = false) {
    if (!dialog) return;

    const tabs = Array.from(dialog.querySelectorAll('[data-frk-spec-tab]'));
    const panels = Array.from(dialog.querySelectorAll('[data-frk-spec-panel]'));
    if (!tabs.length || !panels.length) return;

    let activeTab = tabs.find((tab) => tab.dataset.frkSpecTab === target);
    if (!activeTab) {
      const fallback = dialog.dataset.frkSpecDefault;
      activeTab = tabs.find((tab) => tab.dataset.frkSpecTab === fallback) || tabs[0];
    }

    const activeTarget = activeTab.dataset.frkSpecTab;

    tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      const selected = panel.dataset.frkSpecPanel === activeTarget;
      panel.hidden = !selected;
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
    });

    dialog.dataset.frkSpecActive = activeTarget;
    resetScrollPosition(dialog);

    if (focusTab) {
      activeTab.focus({ preventScroll: true });
    }
  }

  // Price per m² is stored ex VAT; show it in the shopper's Ex/Inc VAT mode (assets/vat-global.js).
  function renderPricePerM2(root) {
    const mode = window.__VAT?.getMode?.() || 'inc';
    const rate = parseFloat(document.documentElement.getAttribute('data-vat-rate'));
    const vat = Number.isFinite(rate) && rate >= 0 ? rate : 0.2;

    root.querySelectorAll('[data-frk-ppm2-ex]').forEach((el) => {
      const ex = parseFloat(el.dataset.frkPpm2Ex);
      if (!Number.isFinite(ex) || ex <= 0) return;
      const value = mode === 'inc' ? ex * (1 + vat) : ex;
      el.textContent = `£${value.toFixed(2)} ${mode === 'inc' ? 'inc.' : 'ex.'} VAT`;
    });
  }

  function openDialog(dialog, opener, target, viaPointer) {
    if (!dialog) return;

    renderPricePerM2(dialog);
    dialog.__frkLastOpener = opener || document.activeElement;
    dialog.__frkRestoreFocus = !viaPointer;
    activate(dialog, target);

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    lockRoot().classList.add(BODY_CLASS);

    window.requestAnimationFrame(() => {
      const activeTab = dialog.querySelector('[data-frk-spec-tab][aria-selected="true"]');
      activeTab?.focus({ preventScroll: true });
      ensureScrollCue(dialog);
      updateScrollCue(dialog);
    });
  }

  function closeDialog(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    lockRoot().classList.remove(BODY_CLASS); document.body.classList.remove(BODY_CLASS);
    forgetSheetScroller(dialog);

    const opener = dialog.__frkLastOpener;
    if (dialog.__frkRestoreFocus && opener && typeof opener.focus === 'function') {
      window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
    } else {
      window.requestAnimationFrame(() => releaseOpenerFocus(dialog));
    }
  }

  // iOS Safari keeps routing the next swipe to the box that was last scrolled ("scroll latching"), even once the
  // sheet has closed, so the collection appears stuck (most often after reading the sheet and tapping the X).
  // Reset the sheet's scroll box and re-attach the closed dialog so WebKit drops it as a scroll target.
  function forgetSheetScroller(dialog) {
    const content = dialog.querySelector('.frk-spec-sheet__content');
    if (content) content.scrollTop = 0;
    window.requestAnimationFrame(() => {
      if (dialog.open || !dialog.parentNode) return;
      const parent = dialog.parentNode;
      const next = dialog.nextSibling;
      parent.removeChild(dialog);
      parent.insertBefore(dialog, next);
    });
  }

  // Touch/tap close: the browser hands focus back to the opener, which on collection cards is the sideways-
  // scrolling badge strip; a focused scroll box can capture the next scroll, so give focus back to the page.
  function releaseOpenerFocus(dialog) {
    if (!dialog || dialog.__frkRestoreFocus) return;
    const opener = dialog.__frkLastOpener;
    const active = document.activeElement;
    if (active && active !== document.body && opener && (active === opener || opener.contains(active) || active.contains(opener))) active.blur();
  }

  function ensureCollectionDialog() {
    let dialog = document.getElementById(COLLECTION_DIALOG_ID);
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'frk-spec-sheet frk-spec-sheet--collection';
    dialog.id = COLLECTION_DIALOG_ID;
    dialog.dataset.frkSpecDialog = '';
    dialog.setAttribute('aria-labelledby', 'frk-collection-spec-heading');
    dialog.innerHTML = `
      <div class="frk-spec-sheet__panel">
        <div class="frk-spec-sheet__handle" aria-hidden="true"></div>
        <header class="frk-spec-sheet__header">
          <h2 id="frk-collection-spec-heading">Product specifications</h2>
          <button type="button" class="frk-spec-sheet__x" data-frk-spec-close aria-label="Close product specifications">${ICONS.close}</button>
        </header>
        <p class="frk-spec-sheet__collection-product" data-frk-collection-product-title></p>
        <div class="frk-spec-sheet__tabs" role="tablist" aria-label="Choose a product specification" data-frk-collection-tabs></div>
        <div class="frk-spec-sheet__content" data-frk-collection-content></div>
        <footer class="frk-spec-sheet__actions frk-spec-sheet__actions--collection">
          <a class="frk-spec-sheet__product-link" data-frk-collection-product-link href="#">View product ${ICONS.external}</a>
          <button type="button" data-frk-spec-close>Close</button>
        </footer>
      </div>`;

    document.body.appendChild(dialog);
    return dialog;
  }

  function tabMarkup(target, label, iconClass = '') {
    return `
      <button class="frk-spec-sheet__tab" type="button" role="tab"
        id="frk-collection-tab-${target}" aria-controls="frk-collection-panel-${target}"
        aria-selected="false" tabindex="-1" data-frk-spec-tab="${target}">
        <span class="frk-spec-sheet__tab-icon ${iconClass}" aria-hidden="true">${ICONS[target].replace('width="30" height="30"', 'width="15" height="15"')}</span>
        <span>${escapeHtml(label)}</span>
      </button>`;
  }

  function panelMarkup(target, title, heroLabel, body, note) {
    const heroClass = `frk-spec-sheet__hero--${target}`;
    return `
      <section class="frk-spec-sheet__tabpanel" id="frk-collection-panel-${target}" role="tabpanel"
        aria-labelledby="frk-collection-tab-${target}" data-frk-spec-panel="${target}" hidden tabindex="0">
        <h3 class="frk-spec-sheet__panel-title">${escapeHtml(title)}</h3>
        <div class="frk-spec-sheet__hero ${heroClass}">
          <span class="frk-spec-sheet__hero-icon" aria-hidden="true">${ICONS[target]}</span>
          <strong>${escapeHtml(heroLabel)}</strong>
        </div>
        ${body}
        <div class="frk-spec-sheet__note">${escapeHtml(note)}</div>
      </section>`;
  }

  function isDouble450(weight) {
    const raw = String(weight || '').trim().toLowerCase();
    return /^900(?:\.0+)?(?:\D|$)/.test(raw)
      || raw.includes('balcony')
      || /2\s*[x×]\s*450/.test(raw);
  }

  function weightLabel(weight) {
    return isDouble450(weight) ? '2 × 450g layers' : `${weight}g`;
  }

  function weightPanel(weight) {
    if (isDouble450(weight)) {
      return panelMarkup(
        'weight',
        '2 × 450g layers',
        '2 layers of 450g/m² chopped strand mat',
        '<p>This kit uses two layers of 450g/m² chopped strand mat to form a strong, seamless GRP waterproof laminate.</p><p>Apply both layers as specified in the kit’s installation guide.</p>',
        'Kit quantities are calculated for the selected roof area, including the allowance shown in the kit contents.'
      );
    }

    const safe = escapeHtml(weight);
    let extra = '<p>The reinforcement weight describes the amount of glass fibre used per square metre. The appropriate system depends on the roof’s intended use and expected traffic.</p>';

    if (String(weight).includes('450')) {
      extra = '<p>This is the standard reinforcement specification for most domestic flat-roof applications and normal maintenance access.</p><p>For heavier or more frequent foot traffic, consider a 600g or balcony-grade system.</p>';
    } else if (String(weight).includes('600')) {
      extra = '<p>This heavier reinforcement specification provides additional strength and is commonly chosen where more regular foot traffic or a more robust laminate is required.</p>';
    }

    return panelMarkup(
      'weight',
      `${weight}g reinforcement`,
      `${weight}g/m² chopped strand mat`,
      `<p>This kit uses ${safe}g/m² chopped strand mat to form a strong, seamless GRP waterproof laminate.</p>${extra}`,
      'Kit quantities are calculated for the selected roof area, including the allowance shown in the kit contents.'
    );
  }

  function firePanel(fire) {
    const safe = escapeHtml(fire);
    return panelMarkup(
      'fire',
      `${fire} fire performance`,
      fire,
      `<p>${safe} is an external fire-performance classification for roof coverings.</p><p>The classification relates to the complete tested roof build-up—not just one tin or component—including the substrate, laminate and finishing system.</p><p>To achieve the stated performance, the roof must be installed in accordance with the applicable manufacturer’s specification and any relevant building requirements.</p>`,
      'Fire performance can depend on the complete build-up and correct installation.'
    );
  }

  function warrantyPanel(warranty) {
    const safe = escapeHtml(warranty);
    return panelMarkup(
      'warranty',
      `${warranty} materials guarantee`,
      warranty,
      `<p>This product carries a manufacturer-backed materials guarantee of up to ${safe} when installed in accordance with the manufacturer’s requirements.</p><p>The guarantee applies to the materials supplied and remains subject to the relevant manufacturer’s terms, conditions, product registration requirements and installation specification.</p><p>Workmanship is normally the responsibility of the installer unless a separate workmanship guarantee has been provided.</p>`,
      'Keep proof of purchase and follow the manufacturer’s installation and registration requirements.'
    );
  }

  // Mirrors the Liquid coverage panel in snippets/product-card-meta-pills.liquid.
  function coveragePanel(coverage, ppm2Ex, kgPerM2, coverage600) {
    const safe = escapeHtml(coverage);
    const stats = [
      ppm2Ex ? `<div><dt>Cost per m²</dt><dd data-frk-ppm2-ex="${escapeHtml(ppm2Ex)}"></dd></div>` : '',
      kgPerM2 ? `<div><dt>Approx. usage${coverage600 ? ' (450g mat)' : ''}</dt><dd>${escapeHtml(kgPerM2)}kg per m²</dd></div>` : ''
    ].join('');
    // Resin: quoted for 450g mat; 600g mat uses resin in proportion to its extra glass.
    const intro = coverage600
      ? `<p>Based on a roof laminated with 450g/m² chopped strand mat, this pack covers up to ${safe}m². (On 600g/m² mat expect around ${escapeHtml(coverage600)}m², about 25% less: resin is used in proportion to the weight of glass, and 600g mat carries a third more glass.)</p><p>Actual coverage`
      : `<p>This pack covers up to ${safe}m². Actual coverage`;

    return panelMarkup(
      'coverage',
      `${coverage}m² coverage`,
      `Covers up to ${coverage}m²`,
      `${stats ? `<dl class="frk-spec-sheet__stats">${stats}</dl>` : ''}${intro} depends on the substrate, temperature, application thickness and waste, so we recommend allowing around 10% extra.</p><p><a class="frk-spec-sheet__link" href="/#frk-roof-calculator">Not sure of your roof area? Use the roof size calculator</a></p>`,
      'Coverage is a guide based on the pack size. Always follow the manufacturer’s application instructions.'
    );
  }

  function populateCollectionDialog(dialog, group) {
    const weight = (group.dataset.frkProductWeight || '').trim();
    const fire = (group.dataset.frkProductFire || '').trim();
    const warranty = (group.dataset.frkProductWarranty || '').trim();
    const coverage = (group.dataset.frkProductCoverage || '').trim();
    const rawTitle = (group.dataset.frkProductTitle || 'Product').trim();
    const productTitle = isDouble450(weight)
      ? rawTitle.replace(/\b900\s*g\b/gi, '2 × 450g layers')
      : rawTitle;
    const productUrl = group.dataset.frkProductUrl || '#';

    const tabs = [];
    const panels = [];

    if (coverage) {
      tabs.push(tabMarkup('coverage', `${coverage}m²`, 'frk-spec-sheet__tab-icon--coverage'));
      panels.push(coveragePanel(coverage, (group.dataset.frkProductPpm2Ex || '').trim(), (group.dataset.frkProductKgPerM2 || '').trim(), (group.dataset.frkProductCoverageMat600 || '').trim()));
    }
    if (weight) {
      tabs.push(tabMarkup('weight', weightLabel(weight)));
      panels.push(weightPanel(weight));
    }
    if (fire) {
      tabs.push(tabMarkup('fire', fire, 'frk-spec-sheet__tab-icon--fire'));
      panels.push(firePanel(fire));
    }
    if (warranty) {
      tabs.push(tabMarkup('warranty', warranty, 'frk-spec-sheet__tab-icon--warranty'));
      panels.push(warrantyPanel(warranty));
    }

    dialog.querySelector('[data-frk-collection-tabs]').innerHTML = tabs.join('');
    dialog.querySelector('[data-frk-collection-content]').innerHTML = panels.join('');
    dialog.querySelector('[data-frk-collection-product-title]').textContent = productTitle;

    const productLink = dialog.querySelector('[data-frk-collection-product-link]');
    productLink.href = productUrl;
    productLink.setAttribute('aria-label', `View ${productTitle}`);

    const firstTarget = coverage ? 'coverage' : weight ? 'weight' : fire ? 'fire' : 'warranty';
    dialog.dataset.frkSpecDefault = firstTarget;
  }

  // Capture collection-card clicks before a card-level link or click handler can navigate.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-frk-collection-spec-open]');
    if (!opener) return;

    event.preventDefault();
    event.stopPropagation();

    const group = opener.closest('[data-frk-collection-spec-group]');
    if (!group) return;

    const dialog = ensureCollectionDialog();
    populateCollectionDialog(dialog, group);
    openDialog(dialog, opener, opener.dataset.frkSpecTarget, event.detail > 0);
  }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-frk-spec-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.frkSpecOpen);
      openDialog(dialog, opener, opener.dataset.frkSpecTarget, event.detail > 0);
      return;
    }

    const tab = event.target.closest('[data-frk-spec-tab]');
    if (tab) {
      const dialog = tab.closest('[data-frk-spec-dialog]');
      activate(dialog, tab.dataset.frkSpecTab);
      return;
    }

    const closer = event.target.closest('[data-frk-spec-close]');
    if (closer) {
      closeDialog(closer.closest('[data-frk-spec-dialog]'));
      return;
    }

    const dialog = event.target.closest('[data-frk-spec-dialog]');
    if (dialog && event.target === dialog) {
      closeDialog(dialog);
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-frk-spec-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const dialog = tab.closest('[data-frk-spec-dialog]');
    const tabs = Array.from(dialog.querySelectorAll('[data-frk-spec-tab]'));
    const index = tabs.indexOf(tab);
    let nextIndex = index;

    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;

    event.preventDefault();
    activate(dialog, tabs[nextIndex].dataset.frkSpecTab, true);
  });

  document.addEventListener('cancel', (event) => {
    if (event.target.matches('[data-frk-spec-dialog]')) {
      event.preventDefault();
      closeDialog(event.target);
    }
  });

  window.addEventListener('vat:modeChanged', () => {
    document.querySelectorAll('[data-frk-spec-dialog][open]').forEach(renderPricePerM2);
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-frk-spec-dialog][open]').forEach(updateScrollCue);
  }, { passive: true });

  document.addEventListener('close', (event) => {
    if (event.target.matches?.('[data-frk-spec-dialog]')) {
      lockRoot().classList.remove(BODY_CLASS); document.body.classList.remove(BODY_CLASS);
      // 'close' fires after the browser has restored focus (e.g. Escape in Chrome), so release it again here
      window.setTimeout(() => releaseOpenerFocus(event.target), 0);
    }
  }, true);
})();
